import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import type { Plan } from "@/lib/finance-plans";
import { estimateEligibility } from "@/lib/eligibility";
import { track } from "@/lib/analytics";

interface BNPLProviderSelectionProps {
  amountAED: number;
  recommendedPlans: Plan[];
  onBack: () => void;
  onSelect: (plan: Plan) => void;
}

export default function BNPLProviderSelection({ amountAED, recommendedPlans, onBack, onSelect }: BNPLProviderSelectionProps) {
  const handlePlanSelect = (plan: Plan) => {
    track("plan_recommended", { planId: plan.id, amountAED });
    onSelect(plan);
  };
  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div>
          <h3 className="text-xl font-semibold">Choose Your Payment Plan</h3>
          <p className="text-sm text-muted-foreground">
            Total Amount: AED {amountAED.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {recommendedPlans.map((plan) => (
          <Card key={plan.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary/20">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-lg">{plan.label}</h4>
                  {(() => {
                    const eligibilityAmount = plan.breakdown.bnplAED || amountAED;
                    const tag = estimateEligibility(eligibilityAmount);
                    return (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${tag==="good"?"bg-green-100 text-green-700":tag==="borderline"?"bg-amber-100 text-amber-700":"bg-gray-100 text-gray-600"}`}>
                        {tag === "good" ? "Likely instant approval" : tag === "borderline" ? "Manual review likely" : "Check eligibility"}
                      </span>
                    );
                  })()}
                </div>
                {plan.notes && plan.notes.length > 0 && (
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    {plan.notes.map((note, idx) => (
                      <li key={idx}>• {note}</li>
                    ))}
                  </ul>
                )}
              </div>
              <Button onClick={() => handlePlanSelect(plan)}>
                Select Plan
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {plan.breakdown.bnplAED && (
                <div className="bg-blue-50 p-3 rounded">
                  <div className="font-medium text-blue-900">BNPL Amount</div>
                  <div className="text-blue-700">AED {plan.breakdown.bnplAED.toLocaleString()}</div>
                </div>
              )}
              
              {plan.breakdown.bankFinanceAED && (
                <div className="bg-green-50 p-3 rounded">
                  <div className="font-medium text-green-900">Bank Finance</div>
                  <div className="text-green-700">AED {plan.breakdown.bankFinanceAED.toLocaleString()}</div>
                </div>
              )}
              
              {plan.breakdown.upfrontAED && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-medium text-gray-900">Upfront Payment</div>
                  <div className="text-gray-700">AED {plan.breakdown.upfrontAED.toLocaleString()}</div>
                </div>
              )}
            </div>

            {plan.tenureMonths && (
              <div className="mt-3 text-sm text-muted-foreground">
                Average tenure: {plan.tenureMonths} months
                {plan.aprPercent && ` • APR: ${plan.aprPercent}%`}
              </div>
            )}
          </Card>
        ))}
      </div>

      {recommendedPlans.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No financing plans available for this amount.</p>
          <Button variant="outline" onClick={onBack} className="mt-4">
            Go Back
          </Button>
        </div>
      )}
    </Card>
  );
}